import { Link } from "react-router-dom";
import Logo from "../ui/Logo";
import { cmsApi } from "@/api/cms";
import { defaultNavItems } from "@/repository/navigation";
import { useQuery } from "@tanstack/react-query";
import { defaultLegalLinks, defaultSocialLinks } from "@/repository/footer";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSelector from "../ui/LanguageSelector";

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { language } = useLanguage();

  const { data: footerItems = defaultNavItems } = useQuery({
    queryKey: ["navigation", "footer-nav", language],
    queryFn: () => cmsApi.getNavigation("footer-nav", language),
  });

  const { data: socialLinks = defaultSocialLinks } = useQuery({
    queryKey: ["social-links", language],
    queryFn: () => cmsApi.getNavigation("social-links", language),
  });

  const { data: legalLinks = defaultLegalLinks } = useQuery({
    queryKey: ["navigation", "legal-links", language],
    queryFn: () => cmsApi.getNavigation("legal-links", language),
  });


  return (
    <footer className="bg-white border-t border-gray-200 py-12">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Main Footer Content */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-8">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-2 focus:outline-none"
          >
            <Logo isDark />
          </Link>

          {/* Navigation Links */}
          <nav className="flex flex-wrap justify-center">
            {footerItems.map((item) => (
              <Link
                key={item.title}
                target={item.target}
                to={item.url}
                className="p-4 text-basic hover:text-primary font-medium transition-colors"
              >
                {item.title}
              </Link>
            ))}
          </nav>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          {/* Social Media Icons */}
          <div className="flex space-x-4 order-2 md:order-1">
            {socialLinks?.map((link) => (
              <a
                key={link.title}
                href={link.url}
                target={link.target}
                rel="noopener noreferrer"
                className="text-basic hover:text-primary transition-colors"
                aria-label={link.title}
              >
                {link.iconUrl ? (
                  <img
                    className="w-5 h-5"
                    src={link.iconUrl}
                    alt={link.title}
                  />
                ) : (
                  <span className="text-xs">{link.title}</span>
                )}
              </a>
            ))}
          </div>

          {/* Copyright & Privacy */}
          <div className="flex flex-col md:flex-row items-center gap-4 order-1 md:order-2">
            <p className="text-basic text-sm">&copy; {currentYear} Dabar HR</p>
            {legalLinks.map((link) => (
              <Link
                key={link.title}
                target={link.target}
                to={link.url}
                className="text-basic hover:text-primary text-sm transition-colors"
              >
                {link.title}
              </Link>
            ))}
            <LanguageSelector />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
